'use client';

import { cn, getColorway, getColorwayWithTransparency } from '@/utils/styles';
import React, { useRef, useEffect, useCallback } from 'react';
import { CardsVerticalScrollProps } from './types';
import { Colorways } from '@/types/constants';
import { trackExpandableCardItemToggled } from '../analytics/eventHandlerTracking';
import {
  CardsVerticalScrollItemDesktopContent,
  CardsVerticalScrollItemDesktopMedia,
} from './CardsVerticalScrollDesktopItem';
import { useCardsVerticalScrollDesktop } from './useCardsVerticalScrollDesktop';
import { useAnchorNavigation } from './useAnchorNavigation';

const CardsVerticalScrollDesktop = ({
  sectionName,
  transparentBackground,
  displayDividers,
  cards,
}: CardsVerticalScrollProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const cardItems = cards?.items ?? [];
  const lastIndex = cardItems.length - 1;
  const getScrollPositionForIndex = useCallback(
    (index: number) => (index === 0 ? 'start' : index === lastIndex ? 'end' : 'middle'),
    [lastIndex],
  );

  const handleActiveCardChange = useCallback(() => {
    // Defer analytics to avoid blocking the main thread during scroll interactions
    setTimeout(() => {
      trackExpandableCardItemToggled({
        action: 'open',
      });
    }, 0);
  }, []);

  const { containerRef, activeCardIndex, scrollHeight, scrollToCard } = useCardsVerticalScrollDesktop({
    cardCount: cardItems.length,
    onActiveCardChange: handleActiveCardChange,
  });

  const activeColorway = getColorway(
    cardItems[activeCardIndex]?.backgroundColor ?? Colorways.DarkGreenGradientLeftToRight,
  );

  // Scroll to a specific card index for anchor navigation (stable callback)
  const handleAnchorNavigation = useCallback(
    (index: number) => {
      scrollToCard(index, getScrollPositionForIndex(index));
    },
    [getScrollPositionForIndex, scrollToCard],
  );

  // Handle anchor navigation for deep linking to specific cards
  useAnchorNavigation({
    cards: cardItems,
    scrollToIndex: handleAnchorNavigation,
  });

  const handleCardClick = useCallback(
    (index: number) => {
      scrollToCard(index, getScrollPositionForIndex(index));
    },
    [getScrollPositionForIndex, scrollToCard],
  );

  // Initialize card refs array
  useEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, cardItems.length);
  }, [cardItems.length]);

  // Keyboard navigation for Up/Down arrows
  // Position at boundaries enables immediate page scroll on next key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't intercept keyboard navigation in interactive elements (inputs, textareas, etc.)
      const target = event.target as HTMLElement;
      const isInteractiveElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;

      if (isInteractiveElement) return;

      // Only intercept when the sticky container is fully visible in the viewport
      const stickyContainer = scrollContainerRef.current;
      if (!stickyContainer) return;

      const rect = stickyContainer.getBoundingClientRect();
      const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isFullyVisible) return;

      if (event.key === 'ArrowUp' && activeCardIndex > 0) {
        event.preventDefault();
        const targetIndex = activeCardIndex - 1;
        scrollToCard(targetIndex, getScrollPositionForIndex(targetIndex));
      } else if (event.key === 'ArrowDown' && activeCardIndex < lastIndex) {
        event.preventDefault();
        const targetIndex = activeCardIndex + 1;
        scrollToCard(targetIndex, getScrollPositionForIndex(targetIndex));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeCardIndex, getScrollPositionForIndex, lastIndex, scrollToCard]);

  const internalClassNames = {
    wrapper: cn('relative', getColorwayWithTransparency(Colorways.DarkGreenGradientLeftToRight, transparentBackground)),
    stickyContainer: cn('sticky top-0 min-h-screen h-auto flex items-center justify-center', 'px-14 py-20'),
    contentWrapper: cn(
      'w-full mx-auto content-wrapper py-14 rounded-3xl overflow-hidden transition-colors duration-300',
      activeColorway,
    ),
    innerContent: cn('flex items-start justify-between gap-4'),
    leftColumn: cn('font-sans grid gap-4 w-[492px]'),
    rightColumn: cn('relative h-[400px] w-[400px] aspect-square', 'ml:w-[596px] ml:h-[596px]'),
  };

  return (
    <div ref={containerRef} id={sectionName} className={internalClassNames.wrapper} data-testid="desktop-version">
      <div style={{ height: scrollHeight }}>
        <div
          ref={scrollContainerRef}
          className={internalClassNames.stickyContainer}
          data-testid="cards-vertical-scroll-sticky-container">
          <div className={internalClassNames.contentWrapper}>
            <div className={internalClassNames.innerContent}>
              {/* Left Column - Accordion Items */}
              <div className={internalClassNames.leftColumn}>
                {cardItems.map((card, index) => (
                  <React.Fragment key={card.sys.id}>
                    {displayDividers && index > 0 && (
                      <hr className="border-t border-current opacity-20" data-testid="card-divider" />
                    )}
                    <CardsVerticalScrollItemDesktopContent
                      card={card}
                      index={index}
                      isActive={activeCardIndex === index}
                      activeBackgroundColor={
                        cardItems[activeCardIndex]?.backgroundColor ?? Colorways.DarkGreenGradientLeftToRight
                      }
                      cardRef={el => {
                        cardRefs.current[index] = el;
                      }}
                      bulletStyle={card.bulletStyle}
                      onClick={() => handleCardClick(index)}
                      id={card.sectionName}
                    />
                  </React.Fragment>
                ))}
              </div>

              {/* Right Column - Media Display */}
              <div className={internalClassNames.rightColumn}>
                {cardItems.map((card, index) => (
                  <CardsVerticalScrollItemDesktopMedia
                    key={`${card.sys.id}-media`}
                    card={card}
                    isActive={activeCardIndex === index}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardsVerticalScrollDesktop;
